import React, { ReactNode, ComponentType, useMemo } from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentProjectIdStore } from './stores/CurrentProjectIdStore';

type CurrentProjectIdContextValue = {
  projectId?: string;
};

/**
 * Context for current project ID.
 */
const CurrentProjectIdContext =
  getOrCreateGlobalContext<CurrentProjectIdContextValue>(
    CONTEXT_NAMES.CurrentProjectIdContext,
  );

export const useCurrentProjectIdStoreWithValidation = () => {
  return useStoreWithValidation<CurrentProjectIdContextValue>(
    CONTEXT_NAMES.CurrentProjectIdContext,
    {
      hook: 'useCurrentProjectIdContext',
      provider: 'CurrentProjectIdProvider',
      context: CurrentProjectIdContext,
    },
  );
};

/**
 * Returns the current project ID from context. Must be used inside `CurrentProjectIdProvider`.
 */
export const useCurrentProjectId = () => {
  const { projectId } = useCurrentProjectIdStoreWithValidation();

  return projectId;
};

type CurrentProjectIdProviderProps = {
  projectId?: string;
  children: ReactNode;
};

export const CurrentProjectIdProvider: ComponentType<
  CurrentProjectIdProviderProps
> = ({ children, projectId: projectIdProp }) => {
  useCurrentProjectIdStore(projectIdProp);

  const value = useMemo(() => ({ projectId: projectIdProp }), [projectIdProp]);

  return (
    <CurrentProjectIdContext.Provider value={value}>
      {children}
    </CurrentProjectIdContext.Provider>
  );
};

CurrentProjectIdProvider.displayName = 'CurrentProjectIdProvider';
